import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { useEffect } from "react";
import { useVariant } from "@~/contexts/VariantContext/VariantContext.tsx";
import { Product } from "@~/types/types.ts";
import { getAnalyticsData } from "@~/product-card-components/__shared__/QuickViewModal/utils.ts";
import { useExperienceString } from "@~/flags/flags";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "quick-view": {
        "data-testid"?: string;
        config?: string;
        "pim-product-id"?: string;
        "initial-variant-id"?: string;
        "analytics-additional-info"?: string;
        "experiences-raw-string"?: string;
      };
    }
  }
}

export type QuickViewProps = {
  product: Product;
  showModal: boolean;
  config?: string;
  modalTitle?: string;
  onClose: () => void;
  originator: "QUICK_VIEW_ICON" | "VIEW_OFFERS" | "ADD_TO_BASKET" | undefined;
};

export function QuickViewModal({
  product,
  showModal,
  config = "standard",
  modalTitle = "",
  onClose,
  originator,
}: QuickViewProps) {
  const { variant } = useVariant();
  const experiencesRawString = useExperienceString();
  const dispatch = useProductCardEvent();

  const isQuickViewEventFromCurrentProduct = (event: Event) => {
    return (
      (event as CustomEvent).detail?.pimProductId ===
      product.aliases.pimProductId
    );
  };

  const dispatchQuickViewOpenedEvent = (event: Event) => {
    if (isQuickViewEventFromCurrentProduct(event)) {
      dispatch(productCardEvents.quickViewOpened, {
        analyticsInfo: (event as CustomEvent).detail,
      });
    }
  };
  const dispatchQuickViewAddToBasketEvent = (event: Event) => {
    if (isQuickViewEventFromCurrentProduct(event)) {
      dispatch(productCardEvents.quickViewAddToBasket, {
        analyticsInfo: (event as CustomEvent).detail,
      });
    }
  };
  const dispatchQuickViewUpdatedEvent = (event: Event) => {
    if (isQuickViewEventFromCurrentProduct(event)) {
      dispatch(productCardEvents.quickViewUpdated, {
        variantId: (event as CustomEvent).detail?.variantId,
        analyticsInfo: (event as CustomEvent).detail,
      });
    }
  };

  useEffect(function loadQuickViewScript() {
    if (
      !window.customElements.get("quick-view") &&
      !document.getElementById("jl-quick-view-script")
    ) {
      const script = document.createElement("script");
      script.src = "/quick-view/quick-view.iife.js";
      script.id = "jl-quick-view-script";
      document.head.appendChild(script);
    }
  }, []);

  useEffect(
    function handleQuickViewEvents() {
      window.addEventListener("jl.quick-view.exit", onClose);
      window.addEventListener(
        "jl.quick-view.opened",
        dispatchQuickViewOpenedEvent,
      );
      window.addEventListener(
        "jl.quick-view.addToBasketSuccess",
        dispatchQuickViewAddToBasketEvent,
      );
      window.addEventListener(
        "jl.quick-view.colourSelected",
        dispatchQuickViewUpdatedEvent,
      );
      window.addEventListener(
        "jl.quick-view.sizeSelected",
        dispatchQuickViewUpdatedEvent,
      );
      return function unsubscribeFromQuickViewEvents() {
        window.removeEventListener("jl.quick-view.exit", onClose);
        window.removeEventListener(
          "jl.quick-view.opened",
          dispatchQuickViewOpenedEvent,
        );
        window.removeEventListener(
          "jl.quick-view.addToBasketSuccess",
          dispatchQuickViewAddToBasketEvent,
        );
        window.removeEventListener(
          "jl.quick-view.colourSelected",
          dispatchQuickViewUpdatedEvent,
        );
        window.removeEventListener(
          "jl.quick-view.sizeSelected",
          dispatchQuickViewUpdatedEvent,
        );
      };
    },
    [onClose],
  );

  const analyticsData = getAnalyticsData(product, originator);

  return (
    <Modal
      isOpen={showModal}
      footer={false}
      onClose={onClose}
      width="extra-large"
      height="fixed"
      title={modalTitle}
    >
      {showModal && (
        <quick-view
          data-testid="quick-view"
          config={config}
          pim-product-id={product.aliases.pimProductId}
          initial-variant-id={variant?.id}
          analytics-additional-info={JSON.stringify(analyticsData)}
          experiences-raw-string={experiencesRawString}
        ></quick-view>
      )}
    </Modal>
  );
}
